import React, { useState, useEffect, useRef } from "react";
import {
  FileText,
  Mail,
  Code,
  MessageSquare,
  Edit2,
  Copy,
  Check,
  Sparkles,
  Trash2,
  Lock,
  Loader2,
  AlertTriangle,
  X,
  ChevronUp,
  ChevronDown,
  Shield,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  CheckCircle2,
  DollarSign,
  ArrowUpRight,
  Target,
  Search,
  Plus,
  Save,
  RefreshCw,
  ExternalLink,
  Maximize,
  Minimize
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { notification } from "antd";
import { ProcessedContentView } from "./ProcessedContentView";
import { TranscriptView } from "./TranscriptView";
import { ConversationView } from "./ConversationView";
import { AIDoctorView } from "./AIDoctorView";
import { AIOptionsModal } from "./AIOptionsModal";
import { RecordingVersions } from "./RecordingVersions";

import { speechService } from "../../services/speechService";
import axiosInstance from "../../config/axios";
import PatientSelector from "../patients/PatientSelector";
import { Patient } from "../../types/patient";

// Separate components to improve readability
const TabButton = ({ id, label, icon, activeTab, onClick }) => (
  <button
    onClick={() => onClick(id)}
    className={`px-3 sm:px-6 py-3 sm:py-4 text-sm font-medium transition-all duration-200 flex items-center gap-1.5 sm:gap-2 relative hover:bg-gray-50/50 ${
      activeTab === id ? "text-blue-600" : "text-gray-600 hover:text-gray-900"
    }`}
  >
    {icon}
    <span className="whitespace-nowrap">{label}</span>
    {activeTab === id && (
      <motion.div
        layoutId="activeTab"
        className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600"
        initial={false}
        transition={{ type: "spring", stiffness: 500, damping: 30 }}
      />
    )}
  </button>
);

const CodeItem = ({ code: rawCode, color, onCopy, onRemove }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  // Safety: if rawCode is not an object, skip rendering
  if (!rawCode || typeof rawCode !== 'object') return null;

  // Normalize code object to handle both API shapes:
  // Old: { code, description, explanation, transcript_evidence }
  // New: { cpt_code/icd_code, description, fee, justification, transcript_evidence }
  const code = {
    code: String(rawCode.code || rawCode.cpt_code || rawCode.icd_code || ""),
    description: String(rawCode.description || ""),
    explanation: String(rawCode.explanation || rawCode.justification || ""),
    transcript_evidence: rawCode.transcript_evidence ? String(rawCode.transcript_evidence) : "",
    fee: rawCode.fee ? String(rawCode.fee) : "",
  };

  const handleCopy = async () => {
    const content = `${code.code} - ${code.description}${code.explanation ? `\nExplanation: ${code.explanation}` : ''}${code.transcript_evidence ? `\nEvidence: "${code.transcript_evidence}"` : ''}`;
    await onCopy(content);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  const colorClasses = {
    '[#0071E3]': {
      bg: 'bg-blue-50',
      border: 'border-blue-100',
      text: 'text-blue-700',
      badge: 'bg-blue-100 text-blue-800',
      hover: 'hover:bg-blue-100/50',
      evidence: 'bg-blue-50 border-blue-200 text-blue-800'
    },
    '[#5856D6]': {
      bg: 'bg-purple-50',
      border: 'border-purple-100', 
      text: 'text-purple-700',
      badge: 'bg-purple-100 text-purple-800',
      hover: 'hover:bg-purple-100/50',
      evidence: 'bg-purple-50 border-purple-200 text-purple-800'
    }
  };

  const colorSet = colorClasses[color] || colorClasses['[#0071E3]'];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className={`group relative ${colorSet.bg} ${colorSet.border} border-2 rounded-xl p-5 transition-all duration-200 hover:shadow-lg ${colorSet.hover} hover:scale-[1.01]`}
    >
      {/* Main Content */}
      <div className="flex items-start gap-4">
        {/* Code Badge */}
        <div className={`${colorSet.badge} px-4 py-2 rounded-lg font-bold text-sm tracking-wide shadow-sm`}>
          {code.code}
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Description */}
          <h3 className="text-gray-900 font-semibold text-base leading-relaxed mb-2">
            {code.description}
          </h3>
          
          {/* Expandable Content */}
          <AnimatePresence>
            {(isExpanded || (!code.explanation && !code.transcript_evidence)) && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="space-y-3"
              >
                {/* Explanation */}
                {code.explanation && (
                  <div className="bg-white/60 border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 bg-amber-400 rounded-full"></div>
                      <span className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                        Clinical Rationale
                      </span>
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      {code.explanation}
                    </p>
                  </div>
                )}

                {/* Transcript Evidence */}
                {code.transcript_evidence && (
                  <div className={`${colorSet.evidence} border-2 rounded-lg p-4`}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                      <span className="text-sm font-semibold uppercase tracking-wide">
                        Supporting Evidence
                      </span>
                    </div>
                    <div className="relative">
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-green-400 to-green-600 rounded-full"></div>
                      <blockquote className="ml-4 text-sm leading-relaxed italic">
                        "{code.transcript_evidence}"
                      </blockquote>
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* Expand/Collapse Button */}
          {(code.explanation || code.transcript_evidence) && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className={`mt-3 inline-flex items-center gap-2 text-sm font-medium ${colorSet.text} hover:underline transition-colors`}
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="w-4 h-4" />
                  Show Less
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4" />
                  Show Details
                </>
              )}
            </button>
          )}
        </div>
        
        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleCopy}
            className={`p-2 rounded-lg transition-all duration-200 ${
              isCopied 
                ? 'bg-green-100 text-green-600' 
                : `${colorSet.bg} ${colorSet.text} hover:bg-white shadow-sm hover:shadow-md`
            }`}
            title={isCopied ? "Copied!" : "Copy code details"}
          >
            {isCopied ? (
              <Check className="w-4 h-4" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
          </motion.button>
          {onRemove && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm(`Remove code ${code.code}?`)) {
                  onRemove();
                }
              }}
              className={`p-2 rounded-lg transition-all duration-200 ${colorSet.bg} text-red-500 hover:bg-red-100 shadow-sm hover:shadow-md`}
              title="Remove code"
            >
              <Trash2 className="w-4 h-4" />
            </motion.button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const NoContentPlaceholder = ({ icon, title, subtitle }) => (
  <div className="text-center text-gray-500 py-16 bg-gray-50 rounded-lg">
    <div className="bg-white p-6 max-w-md mx-auto rounded-xl shadow-sm border border-gray-100">
      {icon}
      <p className="text-xl font-medium text-gray-700 mt-3">{title}</p>
      <p className="text-sm text-gray-400 mt-2">{subtitle}</p>
    </div>
  </div>
);

// Search Results Component
// Common CPT codes for local search — CPT Level I descriptors are AMA-licensed,
// so they are not available from the free NLM Clinical Tables API
const COMMON_CPT_CODES = [
  { code: "99202", description: "Office visit, new patient", explanation: "New patient office evaluation, straightforward complexity (15-29 min)" },
  { code: "99203", description: "Office visit, new patient", explanation: "New patient office evaluation, low complexity (30-44 min)" },
  { code: "99204", description: "Office visit, new patient", explanation: "New patient office evaluation, moderate complexity (45-59 min)" },
  { code: "99205", description: "Office visit, new patient", explanation: "New patient office evaluation, high complexity (60-74 min)" },
  { code: "99211", description: "Office visit, established patient", explanation: "Established patient office visit, minimal presenting problem" },
  { code: "99212", description: "Office visit, established patient", explanation: "Established patient office evaluation, straightforward complexity (10-19 min)" },
  { code: "99213", description: "Office visit, established patient", explanation: "Established patient office evaluation, low complexity (20-29 min)" },
  { code: "99214", description: "Office visit, established patient", explanation: "Established patient office evaluation, moderate complexity (30-39 min)" },
  { code: "99215", description: "Office visit, established patient", explanation: "Established patient office evaluation, high complexity (40-54 min)" },
  { code: "99221", description: "Initial hospital inpatient care", explanation: "Initial hospital care, straightforward/low complexity" },
  { code: "99222", description: "Initial hospital inpatient care", explanation: "Initial hospital care, moderate complexity" },
  { code: "99223", description: "Initial hospital inpatient care", explanation: "Initial hospital care, high complexity" },
  { code: "99231", description: "Subsequent hospital inpatient care", explanation: "Subsequent hospital care, straightforward/low complexity" },
  { code: "99232", description: "Subsequent hospital inpatient care", explanation: "Subsequent hospital care, moderate complexity" },
  { code: "99233", description: "Subsequent hospital inpatient care", explanation: "Subsequent hospital care, high complexity" },
  { code: "99283", description: "Emergency department visit", explanation: "ED visit, moderate severity" },
  { code: "99284", description: "Emergency department visit", explanation: "ED visit, high severity without immediate threat" },
  { code: "99285", description: "Emergency department visit", explanation: "ED visit, high severity with immediate threat" },
  { code: "99381", description: "Preventive visit, new patient (infant)", explanation: "Initial preventive medicine evaluation, under 1 year" },
  { code: "99385", description: "Preventive visit, new patient (18-39)", explanation: "Initial preventive medicine evaluation, age 18-39" },
  { code: "99386", description: "Preventive visit, new patient (40-64)", explanation: "Initial preventive medicine evaluation, age 40-64" },
  { code: "99395", description: "Preventive visit, established patient (18-39)", explanation: "Periodic preventive medicine evaluation, age 18-39" },
  { code: "99396", description: "Preventive visit, established patient (40-64)", explanation: "Periodic preventive medicine evaluation, age 40-64" },
  { code: "99406", description: "Smoking cessation counseling", explanation: "Tobacco use cessation counseling, 3-10 minutes" },
  { code: "99441", description: "Telephone evaluation", explanation: "Telephone E/M service, 5-10 minutes" },
  { code: "99442", description: "Telephone evaluation", explanation: "Telephone E/M service, 11-20 minutes" },
  { code: "90471", description: "Immunization administration", explanation: "Administration of one vaccine (injection)" },
  { code: "93000", description: "Electrocardiogram", explanation: "ECG with at least 12 leads, with interpretation and report" },
  { code: "94010", description: "Spirometry", explanation: "Breathing capacity test with graphic record" },
  { code: "97110", description: "Therapeutic procedure", explanation: "Therapeutic exercises to develop strength and endurance, each 15 min" },
  { code: "97140", description: "Manual therapy", explanation: "Manual therapy techniques, one or more regions, each 15 min" },
  { code: "20610", description: "Arthrocentesis, major joint", explanation: "Aspiration and/or injection of major joint or bursa" },
  { code: "64483", description: "Injection procedure", explanation: "Transforaminal epidural injection, lumbar or sacral, single level" },
  { code: "36415", description: "Venipuncture", explanation: "Collection of venous blood by venipuncture" },
  { code: "81002", description: "Urinalysis", explanation: "Urinalysis, non-automated, without microscopy" },
  { code: "85025", description: "Complete blood count (CBC)", explanation: "CBC with automated differential WBC count" },
  { code: "80053", description: "Comprehensive metabolic panel", explanation: "Comprehensive metabolic panel blood test" },
  { code: "80061", description: "Lipid panel", explanation: "Lipid panel blood test" },
  { code: "83036", description: "Hemoglobin A1c", explanation: "Glycosylated hemoglobin test" },
  { code: "12001", description: "Simple wound repair", explanation: "Simple repair of superficial wounds, 2.5 cm or less" },
  { code: "17110", description: "Destruction of benign lesions", explanation: "Destruction of benign lesions other than skin tags, up to 14" },
  { code: "10060", description: "Incision and drainage of abscess", explanation: "I&D of abscess, simple or single" },
  { code: "71046", description: "Chest X-ray", explanation: "Radiologic examination, chest, 2 views" },
];

const SearchResults = ({ results, onAddCode, codeType, colorTheme, isSearching, hasSearched }) => {
  if (isSearching) {
    return (
      <div className="mt-4 border border-gray-200 rounded-lg bg-white shadow-sm p-4 text-center">
        <p className="text-sm text-gray-500">Searching codes...</p>
      </div>
    );
  }

  if (!results || results.length === 0) {
    if (hasSearched) {
      return (
        <div className="mt-4 border border-gray-200 rounded-lg bg-white shadow-sm p-4 text-center">
          <p className="text-sm text-gray-500">No matching codes found. Try a different search term or add the code manually.</p>
        </div>
      );
    }
    return null;
  }

  return (
    <div className="mt-4 border border-gray-200 rounded-lg bg-white shadow-sm">
      <div className="p-3 border-b border-gray-200 bg-gray-50">
        <h4 className="text-sm font-semibold text-gray-700">Search Results</h4>
      </div>
      <div className="max-h-48 overflow-y-auto">
        {results.map((result, index) => (
          <div key={index} className="p-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`px-2 py-1 text-xs font-mono font-bold rounded ${colorTheme.badge}`}>
                    {result.code}
                  </span>
                </div>
                <p className="text-sm font-medium text-gray-900 mb-1">{result.description}</p>
                {result.explanation && (
                  <p className="text-xs text-gray-600">{result.explanation}</p>
                )}
              </div>
              <button
                onClick={() => onAddCode(result, codeType)}
                className={`flex items-center gap-1 px-3 py-1 text-xs font-medium ${colorTheme.button} rounded-lg transition-all duration-200 hover:shadow-sm`}
              >
                <Plus className="w-3 h-3" />
                Add
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Add Code Form Component
const AddCodeForm = ({ codeType, onAddCode, onCancel, colorTheme }) => {
  const [newCode, setNewCode] = useState({
    code: "",
    description: "",
    explanation: "",
    transcript_evidence: ""
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onAddCode(newCode, codeType);
    setNewCode({ code: "", description: "", explanation: "", transcript_evidence: "" });
  };

  return (
    <div className="mt-4 border border-gray-200 rounded-lg bg-white shadow-sm">
      <div className={`p-3 border-b border-gray-200 ${colorTheme.headerBg}`}>
        <h4 className="text-sm font-semibold text-gray-700">Add New {codeType.toUpperCase()} Code</h4>
      </div>
      <form onSubmit={handleSubmit} className="p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Code *</label>
            <input
              type="text"
              value={newCode.code}
              onChange={(e) => setNewCode(prev => ({ ...prev, code: e.target.value }))}
              placeholder={`Enter ${codeType.toUpperCase()} code`}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Description *</label>
            <input
              type="text"
              value={newCode.description}
              onChange={(e) => setNewCode(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Enter description"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Clinical Explanation</label>
          <textarea
            value={newCode.explanation}
            onChange={(e) => setNewCode(prev => ({ ...prev, explanation: e.target.value }))}
            placeholder="Enter clinical explanation or rationale"
            rows={2}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">Supporting Evidence</label>
          <textarea
            value={newCode.transcript_evidence}
            onChange={(e) => setNewCode(prev => ({ ...prev, transcript_evidence: e.target.value }))}
            placeholder="Enter transcript evidence or supporting documentation"
            rows={2}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div className="flex items-center gap-2 pt-2">
          <button
            type="submit"
            className={`flex items-center gap-1 px-4 py-2 text-sm font-medium ${colorTheme.button} rounded-lg transition-all duration-200 hover:shadow-sm`}
          >
            <Save className="w-4 h-4" />
            Add Code
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export const NotesInterface = ({
  notes: initialNotes,
  onNotesChange,
  onSendEmail,
  onError, // Used only for child modals - internal errors use Ant notifications
  onDelete,
  onDeleteClick,
  isLoading = false,
  title,
  onTitleChange,
  emailContent: initialEmailContent = "",
  readOnly = false,
  selectedRecordingId,
  selectedTemplateId,
  duration,
  onVersionSelect,
  onRefreshRecording,
  selectedTransactionId
}) => {
  // Configure Ant Design notification position
  useEffect(() => {
    notification.config({
      placement: 'topRight',
      duration: 3,
      maxCount: 5,
    });
  }, []);
  
  // State management
  const [activeTab, setActiveTab] = useState("soap");
  const [isSending, setIsSending] = useState(false);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingPatientName, setIsEditingPatientName] = useState(false);
  const [titleError, setTitleError] = useState(false);
  const [patientNameError, setPatientNameError] = useState(false);
  const [patientNameErrorMessage, setPatientNameErrorMessage] = useState("Patient name cannot be empty");
  const [notes, setNotes] = useState(initialNotes);
  const [patientName, setPatientName] = useState(initialNotes?.processing_options?.patient_name || "");
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [localTitle, setLocalTitle] = useState(title);
  const [isUpdating, setIsUpdating] = useState(false);
  const [emailContent, setEmailContent] = useState(initialEmailContent);
  const [emailSubject, setEmailSubject] = useState("");
  const [isLoadingRecording, setIsLoadingRecording] = useState(false);
  const [recordingStatus, setRecordingStatus] = useState("completed");
  const [copyStatus, setCopyStatus] = useState({ isCopied: false, type: "" });

  const [options, setOptions] = useState({
    visit_types: [],
    pronouns: [],
    note_lengths: [],
  });
  const [selectedVisitType, setSelectedVisitType] = useState(
    initialNotes?.processing_options?.visit_type || ""
  );
  const [selectedPronouns, setSelectedPronouns] = useState(
    initialNotes?.processing_options?.pronouns || ""
  );
  const [selectedNoteLength, setSelectedNoteLength] = useState(
    initialNotes?.processing_options?.note_length || ""
  );
  const [isAIOptionsOpen, setIsAIOptionsOpen] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [currentTemplateName, setCurrentTemplateName] = useState("");
  const [isAuditSectionOpen, setIsAuditSectionOpen] = useState(true);
  const [isFeeOptimizationOpen, setIsFeeOptimizationOpen] = useState(true);
  const [isICDSectionOpen, setIsICDSectionOpen] = useState(true);
  const [isCPTSectionOpen, setIsCPTSectionOpen] = useState(true);
  const [isHeaderMinimized, setIsHeaderMinimized] = useState(false);
  const [showTabSwitcher, setShowTabSwitcher] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const tabSwitcherButtonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  // Search and Add Code states
  const [icdSearchTerm, setIcdSearchTerm] = useState("");
  const [cptSearchTerm, setCptSearchTerm] = useState("");
  const [showAddIcdForm, setShowAddIcdForm] = useState(false);
  const [showAddCptForm, setShowAddCptForm] = useState(false);
  const [searchResults, setSearchResults] = useState({ icd: [], cpt: [] });
  const [isSearchingCodes, setIsSearchingCodes] = useState({ icd: false, cpt: false });
  const [hasSearchedCodes, setHasSearchedCodes] = useState({ icd: false, cpt: false });




  // Auto-minimize header when entering full screen
  useEffect(() => {
    if (isFullScreen) {
      setIsHeaderMinimized(true);
    }
  }, [isFullScreen]);

  // Calculate dropdown position when opening
  useEffect(() => {
    if (showTabSwitcher && tabSwitcherButtonRef.current) {
      const rect = tabSwitcherButtonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        left: rect.left
      });
    }
  }, [showTabSwitcher]);

  // Close tab switcher when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showTabSwitcher &&
          tabSwitcherButtonRef.current &&
          !tabSwitcherButtonRef.current.contains(target) &&
          !target.closest('[data-dropdown-menu]')) {
        setShowTabSwitcher(false);
      }
    };

    if (showTabSwitcher) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTabSwitcher]);

  // Force reset all state when selectedRecordingId changes
  useEffect(() => {
    console.log("NotesInterface: Recording ID changed to:", selectedRecordingId);

    // Reset to default tab
    setActiveTab("soap");
    
    // Reset editing state
    setIsEditingTitle(false);
    setIsEditingPatientName(false);
    
    // Reset processing indicators
    setIsLoadingRecording(false);
    setIsSending(false);
    
    // Only force notes update if we have initial notes
    if (initialNotes) {
      setNotes(initialNotes);
      
      // Update patient name if available
      if (initialNotes.processing_options?.patient_name) {
        setPatientName(initialNotes.processing_options.patient_name);
      } else {
        setPatientName("");
      }
      
     
      
      // Update email content if available
      if (initialNotes.patient_instructions?.email) {
        setEmailContent(initialNotes.patient_instructions.email.body || "");
        setEmailSubject(initialNotes.patient_instructions.email.subject || "");
      } else {
        setEmailContent("");
        setEmailSubject("");
      }
      
      // Update dropdown values
      if (initialNotes.processing_options) {
        setSelectedVisitType(initialNotes.processing_options.visit_type || "");
        setSelectedPronouns(initialNotes.processing_options.pronouns || "");
        setSelectedNoteLength(initialNotes.processing_options.note_length || "");
      } else {
        setSelectedVisitType("");
        setSelectedPronouns("");
        setSelectedNoteLength("");
      }
      
      // Set the recording status
      if (initialNotes.processing_status) {
        setRecordingStatus(initialNotes.processing_status.status?.toLowerCase() || "completed");
      } else {
        setRecordingStatus("completed");
      }
      
      setHasChanges(false);
    }
  }, [selectedRecordingId, initialNotes]);

  // Update notes and recording status when initialNotes changes
  useEffect(() => {
    if (initialNotes) {
      setNotes(initialNotes);
      
      // Update patient name if available
      if (initialNotes.processing_options?.patient_name) {
        setPatientName(initialNotes.processing_options.patient_name);
      }
      
    
      if (initialNotes.patient_instructions?.email) {
        setEmailContent(initialNotes.patient_instructions.email.body || "");
        setEmailSubject(initialNotes.patient_instructions.email.subject || "");
      }
      // Update dropdown values
      if (initialNotes.processing_options) {
        setSelectedVisitType(initialNotes.processing_options.visit_type || "");
        setSelectedPronouns(initialNotes.processing_options.pronouns || "");
        setSelectedNoteLength(initialNotes.processing_options.note_length || "");
      }
      
      // Set the recording status
      if (initialNotes.processing_status) {
        setRecordingStatus(initialNotes.processing_status.status?.toLowerCase() || "completed");
      }
      
      setHasChanges(false);
    }
  }, [initialNotes]);

  // Fetch options once on component mount
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const fetchedOptions = await speechService.getOptions();
        setOptions(fetchedOptions);
      } catch (error) {
        console.error("Error fetching options:", error);
        notification.error({
          message: "Failed to fetch options",
          description: "Unable to load processing options. Some features may not be available.",
          placement: "topRight",
          duration: 5
        });
      }
    };
    fetchOptions();
  }, []);

  // Separate useEffect to handle adding selected values to options if they don't exist
  useEffect(() => {
    setOptions((prevOptions) => {
      const updatedOptions = { ...prevOptions };

      if (
        selectedVisitType &&
        !updatedOptions.visit_types.find(
          (opt) => opt.value === selectedVisitType
        )
      ) {
        updatedOptions.visit_types = [
          ...updatedOptions.visit_types,
          { value: selectedVisitType, label: selectedVisitType },
        ];
      }
      if (
        selectedPronouns &&
        !updatedOptions.pronouns.find((opt) => opt.value === selectedPronouns)
      ) {
        updatedOptions.pronouns = [
          ...updatedOptions.pronouns,
          { value: selectedPronouns, label: selectedPronouns },
        ];
      }
      if (
        selectedNoteLength &&
        !updatedOptions.note_lengths.find(
          (opt) => opt.value === selectedNoteLength
        )
      ) {
        updatedOptions.note_lengths = [
          ...updatedOptions.note_lengths,
          { value: selectedNoteLength, label: selectedNoteLength },
        ];
      }

      return updatedOptions;
    });
  }, [selectedVisitType, selectedPronouns, selectedNoteLength]);

  // Set current template name
  useEffect(() => {
    
    if (initialNotes?.template?.name) {
      setCurrentTemplateName(initialNotes.template.name);
    }
  }, [initialNotes?.template?.name]);

  // Handle email sending
  const handleSendEmail = async () => {
    try {
      setIsSending(true);
      await onSendEmail();
      setIsSending(false);
    } catch (error) {
      setIsSending(false);
      notification.error({
        message: "Failed to send email",
        description: error instanceof Error ? error.message : "An error occurred while sending the email. Please try again.",
        placement: "topRight",
        duration: 5
      });
    }
  };

  // Copy content to clipboard with feedback
  const handleCopy = async (content, type = "all") => {
    try {
      await navigator.clipboard.writeText(content);
      // Update copy status for button UI
      setCopyStatus({ isCopied: true, type });
      setTimeout(() => setCopyStatus({ isCopied: false, type: "" }), 2000);
      
      // Show a toast notification
      notification.success({
        message: "Content copied to clipboard!",
        description: "The content has been copied to your clipboard successfully.",
        placement: "topRight",
        duration: 2
      });
    } catch (error) {
      notification.error({
        message: "Failed to copy content to clipboard",
        description: "Please try again or copy manually.",
        placement: "topRight",
        duration: 3
      });
    }
  };

  // Copy all content
  const handleCopyAll = async () => {
    const icdCodes = getICDCodes();
    const cptCodes = getCPTCodes();
    
    const formatCodes = (codes) => {
      return codes.map(code => {
        const codeId = code.code || code.cpt_code || code.icd_code || "";
        const explanation = code.explanation || code.justification || "";
        let codeText = `${codeId} - ${code.description}`;
        if (explanation) {
          codeText += `\n  Explanation: ${explanation}`;
        }
        if (code.transcript_evidence) {
          codeText += `\n  Evidence: "${code.transcript_evidence}"`;
        }
        return codeText;
      }).join("\n\n");
    };

    const content = `
Transcript:
${notes.transcript || "No transcript available"}

Notes:
${JSON.stringify(notes.processed_content, null, 2)}

ICD Codes:
${icdCodes?.length > 0 ? formatCodes(icdCodes) : "No ICD codes available"}

CPT Codes:
${cptCodes?.length > 0 ? formatCodes(cptCodes) : "No CPT codes available"}

Email:
Subject: ${notes.patient_instructions?.email?.subject || ""}
Body: ${notes.patient_instructions?.email?.body || ""}
    `;
    handleCopy(content, "all");
  };

  // Copy active tab content
  const handleCopyActiveTab = () => {
    let content = "";
    switch (activeTab) {
      case "soap":
        const processedContent = notes.processed_content?.scribed || 
                                notes.processed_content?.content || 
                                notes.processed_content;
        
        if (processedContent) {
          content = Object.entries(processedContent)
            .map(([key, value]) => {
              const formattedKey = key
                .split("_")
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ");

              let formattedValue = "";
              if (typeof value === "object" && value !== null) {
                formattedValue = Object.entries(value)
                  .map(([subKey, subValue]) => {
                    const formattedSubKey = subKey
                      .split("_")
                      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                      .join(" ");
                    
                    return `${formattedSubKey}: ${
                      Array.isArray(subValue)
                        ? "\n" + subValue.map(item => `• ${item}`).join("\n")
                        : subValue
                    }`;
                  })
                  .join("\n");
              } else {
                formattedValue = String(value || "");
              }

              return `${formattedKey}:\n${formattedValue}`;
            })
            .join("\n\n");
        }
        break;
      case "email":
        // Get the most reliable source of email data
        const emailData = getEmailContent();
        content = `Subject: ${emailData.subject || ""}\nBody: ${emailData.body || ""}`;
        break;
      case "codes":
        // Get the codes directly from our getter functions
        const icdCodes = getICDCodes();
        const cptCodes = getCPTCodes();

        // Format ICD codes with enhanced information
        content = "ICD Codes:";
        if (icdCodes && icdCodes.length > 0) {
          content += "\n" + icdCodes.map(code => {
            const codeId = code.code || code.icd_code || "";
            const explanation = code.explanation || code.justification || "";
            let codeText = `${codeId} - ${code.description}`;
            if (explanation) {
              codeText += `\n  Explanation: ${explanation}`;
            }
            if (code.transcript_evidence) {
              codeText += `\n  Evidence: "${code.transcript_evidence}"`;
            }
            return codeText;
          }).join("\n\n");
        } else {
          content += "\nNo ICD codes available";
        }

        // Format CPT codes with enhanced information
        content += "\n\nCPT Codes:";
        if (cptCodes && cptCodes.length > 0) {
          content += "\n" + cptCodes.map(code => {
            const codeId = code.code || code.cpt_code || "";
            const explanation = code.explanation || code.justification || "";
            let codeText = `${codeId} - ${code.description}`;
            if (explanation) {
              codeText += `\n  Explanation: ${explanation}`;
            }
            if (code.transcript_evidence) {
              codeText += `\n  Evidence: "${code.transcript_evidence}"`;
            }
            return codeText;
          }).join("\n\n");
        } else {
          content += "\nNo CPT codes available";
        }
        break;
      case "transcript":
        content = notes?.transcript || "No transcript available";
        break;
      case "conversation":
        // Format conversation - check both possible locations
        if (notes?.processed_content?.conversation) {
          content = notes.processed_content.conversation
            .map(item => `${item.speaker || item.role || "Unknown"}: ${item.dialogue || item.text || ""}`).join("\n\n");
        } else {
          content = "No conversation available";
        }
        break;
      case "aidoctor":
        // Format AI Doctor data
        const aiData = getAIDoctorContent();
        if (aiData) {
          content = Object.entries(aiData)
            .map(([key, value]) => {
              const formattedKey = key
                .split("_")
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ");
              
              let formattedValue = "";
              if (Array.isArray(value)) {
                formattedValue = value.map(item => `• ${item}`).join("\n");
              } else if (typeof value === "object" && value !== null) {
                formattedValue = Object.entries(value)
                  .map(([subKey, subValue]) => {
                    const formattedSubKey = subKey
                      .split("_")
                      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                      .join(" ");
                    
                    if (Array.isArray(subValue)) {
                      return `${formattedSubKey}:\n${subValue.map(item => `  • ${item}`).join("\n")}`;
                    }
                    
                    return `${formattedSubKey}: ${subValue}`;
                  })
                  .join("\n");
              } else {
                formattedValue = String(value || "");
              }
              
              return `${formattedKey}:\n${formattedValue}`;
            })
            .join("\n\n");
        } else {
          content = "No AI Doctor information available";
        }
        break;
    }
    handleCopy(content, activeTab);
  };

  


  // Helper function to get processed content from the notes object
  const getProcessedContent = () => {
    if (!notes.processed_content) return null;
    
    // First try to get content from scribed, then from content, then use the whole object
    if (notes.processed_content.scribed && Object.keys(notes.processed_content.scribed).length > 0) {
      return notes.processed_content.scribed;
    } else if (notes.processed_content.content && Object.keys(notes.processed_content.content).length > 0) {
      return notes.processed_content.content;
    } else {
      // Only return other processed_content fields if they contain SOAP-specific data
      // This prevents showing previous notes when scribed is null
      const soapFields = ['subjective', 'objective', 'assessment', 'plan', 'red_flags', 'follow_up'];
      
      // Check if any SOAP fields exist directly in processed_content
      const hasSoapFields = Object.keys(notes.processed_content).some(key => 
        soapFields.includes(key) && notes.processed_content[key] !== null && notes.processed_content[key] !== undefined
      );
      
      if (hasSoapFields) {
        // Filter to include only SOAP-related fields
        const soapContent = {};
        soapFields.forEach(field => {
          if (notes.processed_content[field]) {
            soapContent[field] = notes.processed_content[field];
          }
        });
        return Object.keys(soapContent).length > 0 ? soapContent : null;
      }
      
      // If no SOAP fields found, return null to show "No notes available"
      return null;
    }
  };

  // Get the processed content from notes
  const processedContent = getProcessedContent();

  // Get ICD codes
  const getICDCodes = () => {
    const codes = notes?.processed_content?.icd_codes || notes?.icd_codes || [];
    return Array.isArray(codes) ? codes.filter(c => c && typeof c === 'object') : [];
  };

  // Get CPT codes
  const getCPTCodes = () => {
    const codes = notes?.processed_content?.cpt_codes || notes?.cpt_codes || [];
    return Array.isArray(codes) ? codes.filter(c => c && typeof c === 'object') : [];
  };

  // Get email content
  const getEmailContent = () => {
    return {
      subject: notes?.processed_content?.patient_instructions?.email?.subject || 
               notes?.patient_instructions?.email?.subject || "",
      body: notes?.processed_content?.patient_instructions?.email?.body || 
            notes?.patient_instructions?.email?.body || ""
    };
  };

  // Get AI Doctor content
  const getAIDoctorContent = () => {
    return notes?.processed_content?.aiDoctor;
  };

  // Get Audit Analysis content
  const getAuditAnalysis = () => {
    return notes?.processed_content?.audit_analysis;
  };

  // Search medical codes via the NLM Clinical Tables API
  // (CPT Level I descriptors are AMA-licensed and not in the free API, so CPT
  // search combines a local list of common CPT codes with HCPCS Level II results)
  const searchMedicalCodes = async (searchTerm, codeType) => {
    if (!searchTerm.trim()) return;

    setIsSearchingCodes(prev => ({ ...prev, [codeType]: true }));
    try {
      let results = [];
      if (codeType === 'icd') {
        const response = await fetch(
          `https://clinicaltables.nlm.nih.gov/api/icd10cm/v3/search?sf=code,name&maxList=25&terms=${encodeURIComponent(searchTerm)}`
        );
        if (!response.ok) throw new Error(`ICD search failed (${response.status})`);
        const data = await response.json();
        results = (data[3] || []).map(([code, name]) => ({
          code,
          description: name,
          explanation: "",
        }));
      } else {
        const query = searchTerm.toLowerCase();
        const localMatches = COMMON_CPT_CODES.filter(item =>
          item.code.toLowerCase().includes(query) ||
          item.description.toLowerCase().includes(query) ||
          (item.explanation && item.explanation.toLowerCase().includes(query))
        );
        let hcpcsMatches = [];
        try {
          const response = await fetch(
            `https://clinicaltables.nlm.nih.gov/api/hcpcs/v3/search?maxList=25&terms=${encodeURIComponent(searchTerm)}`
          );
          if (response.ok) {
            const data = await response.json();
            hcpcsMatches = (data[3] || []).map(([code, display]) => ({
              code,
              description: display,
              explanation: "HCPCS Level II",
            }));
          }
        } catch (hcpcsError) {
          console.error('HCPCS search failed, using local results only:', hcpcsError);
        }
        results = [...localMatches, ...hcpcsMatches];
      }

      setSearchResults(prev => ({
        ...prev,
        [codeType]: results
      }));
      setHasSearchedCodes(prev => ({ ...prev, [codeType]: true }));
    } catch (error) {
      console.error('Error searching codes:', error);
      notification.error({
        message: 'Search Error',
        description: 'Failed to search medical codes. Please try again.',
      });
    } finally {
      setIsSearchingCodes(prev => ({ ...prev, [codeType]: false }));
    }
  };

  // Add new code to the list and persist it to the backend
  const addCodeToList = async (newCode, codeType) => {
    if (!newCode.code || !newCode.description) {
      notification.error({
        message: 'Validation Error',
        description: 'Code and description are required fields.',
      });
      return;
    }

    const codesField = codeType === 'icd' ? 'icd_codes' : 'cpt_codes';
    const existingCodes = notes?.processed_content?.[codesField] || notes?.[codesField] || [];

    if (existingCodes.some(existing => existing.code === newCode.code)) {
      notification.warning({
        message: 'Duplicate Code',
        description: `${codeType.toUpperCase()} code ${newCode.code} is already in the list.`,
      });
      return;
    }

    const updatedCodes = [...existingCodes, newCode];
    const processedContent = {
      ...(notes?.processed_content || {}),
      [codesField]: updatedCodes,
    };

    // Persist to the backend for saved recordings ("current" is an in-progress
    // recording that has no transcript document yet)
    if (selectedRecordingId && selectedRecordingId !== 'current') {
      try {
        await axiosInstance.put(
          `/api/scribing/speech/recordings/${selectedRecordingId}/transcript`,
          { notes: { processed_content: { [codesField]: updatedCodes } } }
        );
      } catch (error) {
        console.error('Error saving code:', error);
        notification.error({
          message: 'Save Failed',
          description: `Could not save ${codeType.toUpperCase()} code ${newCode.code}. Please try again.`,
        });
        return;
      }
    }

    setNotes(prev => ({
      ...prev,
      processed_content: {
        ...(prev?.processed_content || {}),
        [codesField]: updatedCodes,
      },
    }));

    if (codeType === 'icd') {
      setShowAddIcdForm(false);
      setSearchResults(prev => ({ ...prev, icd: [] }));
      setHasSearchedCodes(prev => ({ ...prev, icd: false }));
      setIcdSearchTerm("");
    } else {
      setShowAddCptForm(false);
      setSearchResults(prev => ({ ...prev, cpt: [] }));
      setHasSearchedCodes(prev => ({ ...prev, cpt: false }));
      setCptSearchTerm("");
    }

    if (onNotesChange) {
      onNotesChange("processed_content", JSON.stringify(processedContent));
    }

    notification.success({
      message: 'Code Added',
      description: `${codeType.toUpperCase()} code ${newCode.code} has been ${
        selectedRecordingId && selectedRecordingId !== 'current' ? 'saved' : 'added'
      } successfully.`,
    });
  };

  // Remove a code from the list and persist the change to the backend
  const removeCodeFromList = async (index, codeType) => {
    const codesField = codeType === 'icd' ? 'icd_codes' : 'cpt_codes';
    const existingCodes = notes?.processed_content?.[codesField] || notes?.[codesField] || [];
    const removedCode = existingCodes[index];
    if (!removedCode) return;

    const updatedCodes = existingCodes.filter((_, i) => i !== index);
    const processedContent = {
      ...(notes?.processed_content || {}),
      [codesField]: updatedCodes,
    };

    if (selectedRecordingId && selectedRecordingId !== 'current') {
      try {
        await axiosInstance.put(
          `/api/scribing/speech/recordings/${selectedRecordingId}/transcript`,
          { notes: { processed_content: { [codesField]: updatedCodes } } }
        );
      } catch (error) {
        console.error('Error removing code:', error);
        notification.error({
          message: 'Remove Failed',
          description: `Could not remove ${codeType.toUpperCase()} code ${removedCode.code || removedCode.icd_code || removedCode.cpt_code || ''}. Please try again.`,
        });
        return;
      }
    }

    setNotes(prev => ({
      ...prev,
      processed_content: {
        ...(prev?.processed_content || {}),
        [codesField]: updatedCodes,
      },
    }));

    if (onNotesChange) {
      onNotesChange("processed_content", JSON.stringify(processedContent));
    }

    notification.success({
      message: 'Code Removed',
      description: `${codeType.toUpperCase()} code ${removedCode.code || removedCode.icd_code || removedCode.cpt_code || ''} has been removed.`,
    });
  };

  // Tabs configuration
  const tabs = [
    {
      id: "soap",
      label: "Notes",
      icon: <FileText className="w-4 h-4" />,
    },
    {
      id: "email",
      label: "Patient Email",
      icon: <Mail className="w-4 h-4" />,
    },
    {
      id: "codes",
      label: "ICD/CPT Codes",
      icon: <Code className="w-4 h-4" />,
    },
    {
      id: "aidoctor",
      label: "AI Doctor",
      icon: <Sparkles className="w-4 h-4" />,
    },
    {
      id: "conversation",
      label: "Conversation",
      icon: <MessageSquare className="w-4 h-4" />,
    },
    {
      id: "transcript",
      label: "Transcript",
      icon: <MessageSquare className="w-4 h-4" />,
    },
  ];

  const icdCodes = getICDCodes();
  const cptCodes = getCPTCodes();
  const emailData = getEmailContent();
  const aiDoctorData = getAIDoctorContent();
  const auditData = getAuditAnalysis();

  
  // Function to update recording title and patient name via API
  const updateRecordingDetails = async (updatedTitle, updatedPatientName) => {
    if (!selectedRecordingId) {
      notification.error({
        message: "Cannot update recording details",
        description: "No recording ID provided. Please make sure a recording is selected.",
        placement: "topRight",
        duration: 5
      });
      return false;
    }
    
    setIsUpdating(true);
    
    try {
      // Prepare the payload
      const payload = {
        title: updatedTitle,
        patient_name: updatedPatientName,
        ...(selectedPatient && { patient_id: selectedPatient.patient_id })
      };
      
      // Make the API call
      const response = await axiosInstance.put(
        `/api/scribing/recordings/${selectedRecordingId}`,
        payload
      );
      
      if (response.status === 200 || response.status === 204) {
        // Show success notification
        notification.success({
          message: "Visit details updated successfully!",
          placement: "topRight",
          duration: 3
        });
        
        // Update state with new values if the API call succeeds
        onTitleChange(updatedTitle);
        
        // Update patient name in processing_options
        const updatedOptions = notes.processing_options 
          ? { ...notes.processing_options, patient_name: updatedPatientName }
          : { patient_name: updatedPatientName };
          
        // Update notes state with the new processing_options
        setNotes({
          ...notes,
          processing_options: updatedOptions
        });
        
        // Notify parent component
        if (onNotesChange) {
          onNotesChange("processing_options", JSON.stringify(updatedOptions));
        }
        
        setHasChanges(true);
        return true;
      } else {
        throw new Error("Failed to update recording details");
      }
    } catch (error) {
      console.error("Error updating recording details:", error);
      const errorMsg = error.response?.data?.message || "Failed to update recording details";
      notification.error({
        message: "Failed to update details",
        description: errorMsg,
        placement: "topRight",
        duration: 5
      });
      return false;
    } finally {
      setIsUpdating(false);
    }
  };

  // Helper function to format duration in a readable format
  const formatDuration = (seconds: number): string => {
    if (!seconds || isNaN(seconds)) return "0 min 0 sec";
    
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    
    const minuteText = minutes === 1 ? "min" : "mins";
    const secondText = remainingSeconds === 1 ? "sec" : "secs";
    
    if (minutes > 0 && remainingSeconds > 0) {
      return `${minutes} ${minuteText} ${remainingSeconds} ${secondText}`;
    } else if (minutes > 0) {
      return `${minutes} ${minuteText}`;
    } else {
      return `${remainingSeconds} ${secondText}`;
    }
  };

  // Handle patient selection
  const handlePatientSelect = (patient: Patient | null, patientName: string) => {
    setSelectedPatient(patient);
    setPatientName(patientName);
    
    // Clear error state when a patient is selected
    if (patient || patientName.trim()) {
      setPatientNameError(false);
      setPatientNameErrorMessage("");
    }
  };
  
  const handleFinishEditingPatientName = async () => {
    // Validate that a patient is selected or name is provided
    if (!selectedPatient && !patientName.trim()) {
      setPatientNameError(true);
      setPatientNameErrorMessage("Please select a patient");
      return; // Don't exit edit mode if validation fails
    }
    
    // Make API call to update both title and patient name
    const success = await updateRecordingDetails(title, patientName);
    
    if (success) {
      setIsEditingPatientName(false);
      setPatientNameError(false);
    }
  };
  
  const handleTitleChange = (value) => {
    setLocalTitle(value);
    
    // Clear error state if input is not empty
    if (value.trim()) {
      setTitleError(false);
    }
  };
  
  const handleFinishEditingTitle = async () => {
    // Validate title is not empty
    if (!localTitle.trim()) {
      setTitleError(true);
      return; // Don't exit edit mode if validation fails
    }
    
    // Make API call to update both title and patient name
    const success = await updateRecordingDetails(localTitle, patientName);
    
    if (success) {
      onTitleChange(localTitle); // Update parent component's title state
      setIsEditingTitle(false);
      setTitleError(false);
    }
  };

  // Effect to keep local title in sync with prop
  useEffect(() => {
    setLocalTitle(title);
  }, [title]);

  // Function to handle retrying a failed recording by refreshing the page
  const handleRetry = () => {
    console.log("Refreshing page to retry failed recording");
    window.location.reload();
  };

  return (
    <div className={`flex flex-col h-full bg-white/70 backdrop-blur-xl rounded-3xl shadow-lg border border-white/50 transition-all duration-300 ${
      isFullScreen ? 'fixed inset-0 z-[9999] rounded-none' : ''
    }`}>
      {/* Header */}
      <div className={`border-b border-[#E5E5EA]/50 px-6 bg-white/80 backdrop-blur-xl shadow-sm transition-all duration-300 ease-in-out ${isHeaderMinimized ? 'py-3 z-[10001]' : 'py-6 z-10'}`}>
        <div className="max-w-[1200px] mx-auto flex items-center justify-between flex-wrap relative">
          <div className="flex items-center gap-4 flex-wrap flex-1">
            <div className="flex flex-col gap-2 max-w-full flex-1">
              {isEditingPatientName ? (
                <div className="flex flex-col gap-2 bg-gradient-to-r from-blue-50 to-blue-100/70 backdrop-blur-xl p-3 rounded-xl shadow-md border border-blue-100 transition-all duration-300 animate-in fade-in-0 zoom-in-95 mb-2 w-full sm:w-auto">
                  <label className="text-xs text-blue-600 font-medium ml-1 flex items-center gap-1">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                    Select Patient
                  </label>
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="w-full sm:w-auto sm:flex-1">
                      <div className="[&_.ant-select]:w-full [&_.ant-select_.ant-select-selector]:!px-3 [&_.ant-select_.ant-select-selector]:!py-2 [&_.ant-select_.ant-select-selector]:!border-blue-200 [&_.ant-select_.ant-select-selector]:!rounded-lg [&_.ant-select_.ant-select-selector]:!shadow-sm [&_.ant-select_.ant-select-selector]:hover:!border-blue-300 [&_.ant-select-focused_.ant-select-selector]:!border-blue-400 [&_.ant-select-focused_.ant-select-selector]:!shadow-[0_0_0_2px_rgba(59,130,246,0.1)]">
                        <PatientSelector
                          selectedPatientId={selectedPatient?.id}
                          selectedPatientName={patientName}
                          onPatientSelect={handlePatientSelect}
                          placeholder="Search and select a patient"
                          size="middle"
                        />
                      </div>
                      {patientNameError && (
                        <p className="text-xs text-red-500 mt-1.5 ml-2 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          {patientNameErrorMessage}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-2 sm:mt-0">
                      <button 
                        onClick={() => {
                          setIsEditingPatientName(false);
                          setPatientNameError(false);
                          // Reset to original patient name and clear selected patient
                          setPatientName(notes?.processing_options?.patient_name || "");
                          setSelectedPatient(null);
                        }}
                        className="px-3 py-1.5 bg-white hover:bg-gray-100 rounded-full text-xs font-medium text-gray-700 transition-all flex items-center gap-1 shadow-sm"
                        disabled={isUpdating}
                      >
                        Cancel
                      </button>
                      <button 
                        onClick={handleFinishEditingPatientName}
                        className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 rounded-full text-xs font-medium text-white transition-all flex items-center gap-1 shadow-sm"
                        disabled={isUpdating}
                      >
                        {isUpdating ? (
                          <>
                            <Loader2 className="w-3 h-3 animate-spin mr-1" />
                            Saving...
                          </>
                        ) : "Save"}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className={`flex items-center gap-2 mb-0 pl-1 flex-wrap transition-all duration-300 ${isHeaderMinimized ? '' : ''}`}>
                  <div className="flex items-center gap-2 rounded-lg px-0 py-1.5 overflow-hidden max-w-full">
                    <p
                      className={`text-sm text-gray-600 cursor-pointer hover:text-blue-600 transition-colors duration-200 flex items-center gap-2 group truncate flex-wrap ${isHeaderMinimized ? 'text-base font-semibold' : ''}`}
                      onClick={() => !isHeaderMinimized && setIsEditingPatientName(true)}
                    >
                      <span className="text-gray-600 font-medium">Patient:</span>
                      <span className="truncate font-medium max-w-[150px] sm:max-w-[200px] md:max-w-[250px]">{patientName || "Unknown"}</span>
                    </p>
                  </div>

                  {/* Active Tab Indicator when minimized */}
                  {isHeaderMinimized && (
                    <div className="relative">
                      <button
                        ref={tabSwitcherButtonRef}
                        onClick={() => setShowTabSwitcher(!showTabSwitcher)}
                        className="flex items-center gap-2 ml-4 bg-blue-50 hover:bg-blue-100 px-3 py-1.5 rounded-full transition-all duration-200 cursor-pointer"
                      >
                        <span className="text-xs font-medium text-blue-700">
                          {activeTab === "soap" ? "📝 Notes" :
                           activeTab === "email" ? "✉️ Email" :
                           activeTab === "codes" ? "🏥 Codes" :
                           activeTab === "transcript" ? "📄 Transcript" :
                           activeTab === "conversation" ? "💬 Conversation" :
                           activeTab === "aidoctor" ? "🤖 AI Doctor" : ""}
                        </span>
                        <ChevronDown className={`w-3 h-3 text-blue-700 transition-transform ${showTabSwitcher ? 'rotate-180' : ''}`} />
                      </button>

                      {/* Tab Switcher Dropdown - Use absolute positioning in normal mode, fixed in full-screen */}
                      {showTabSwitcher && (
                        <div
                          data-dropdown-menu
                          className={`${isFullScreen ? 'fixed' : 'absolute'} top-full mt-2 left-0 bg-white rounded-lg shadow-2xl border border-gray-200 py-2 min-w-[180px] z-[10003] max-h-[400px] overflow-y-auto`}
                          style={isFullScreen ? {
                            top: `${dropdownPosition.top}px`,
                            left: `${dropdownPosition.left}px`
                          } : undefined}
                        >
                          {tabs.map((tab) => (
                            <button
                              key={tab.id}
                              onClick={() => {
                                setActiveTab(tab.id);
                                setShowTabSwitcher(false);
                              }}
                              className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 hover:bg-blue-50 transition-colors ${
                                activeTab === tab.id ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                              }`}
                            >
                              {tab.icon}
                              {tab.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {!isHeaderMinimized && (
                    <button
                      onClick={() => setIsEditingPatientName(true)}
                      className="p-1.5 bg-gray-100 hover:bg-blue-100 rounded-full transition-all flex items-center justify-center group shadow-sm relative hover:scale-110 hover:shadow-md duration-200"
                      aria-label="Edit patient name"
                      title="Edit patient name"
                    >
                      <Edit2 className="w-3.5 h-3.5 text-gray-500 group-hover:text-blue-600" />
                      <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                        Edit patient name
                      </span>
                    </button>
                  )}
                </div>
              )}
            
              {isEditingTitle ? (
                <div className="flex flex-col gap-2 bg-white/80 backdrop-blur-xl p-3 rounded-xl shadow-md border border-gray-100 transition-all duration-300 animate-in fade-in-0 zoom-in-95 w-full sm:max-w-lg">
                  <label htmlFor="title-input" className="text-xs text-gray-500 font-medium ml-1">Edit Title</label>
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="w-full sm:flex-1">
                      <input
                        id="title-input"
                        type="text"
                        value={localTitle}
                        onChange={(e) => handleTitleChange(e.target.value)}
                        className={`text-2xl font-semibold bg-transparent border-b ${titleError ? 'border-red-500' : 'border-gray-300'} outline-none px-2 min-w-[200px] transition-all focus:border-blue-500 w-full`}
                        placeholder="Enter title"
                        autoFocus
                      />
                      {titleError && (
                        <p className="text-xs text-red-500 mt-1 ml-2">Title cannot be empty</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-2 sm:mt-0">
                      <button 
                        onClick={() => {
                          setIsEditingTitle(false);
                          setTitleError(false);
                          setLocalTitle(title); // Reset to original title
                        }}
                        className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-full text-sm font-medium text-gray-700 transition-all flex items-center gap-1 group"
                        disabled={isUpdating}
                      >
                        Cancel
                      </button>
                      <button 
                        onClick={handleFinishEditingTitle}
                        className="px-3 py-1.5 bg-blue-500 hover:bg-blue-600 rounded-full text-sm font-medium text-white transition-all flex items-center gap-1 group"
                        disabled={isUpdating}
                      >
                        {isUpdating ? (
                          <>
                            <Loader2 className="w-3 h-3 animate-spin mr-1" />
                            Saving...
                          </>
                        ) : "Save"}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                !isHeaderMinimized && (
                  <div className="flex items-center gap-2 pl-1 flex-wrap">
                    <div className="flex items-center gap-2 group">
                      <h2
                        className="text-xl sm:text-2xl font-semibold cursor-pointer hover:text-blue-600 transition-colors duration-200 break-words"
                        onClick={() => setIsEditingTitle(true)}
                      >
                        {title}
                      </h2>
                    </div>
                    <button
                      onClick={() => setIsEditingTitle(true)}
                      className="p-1.5 bg-gray-100 hover:bg-blue-100 rounded-full transition-all flex items-center justify-center group shadow-sm relative hover:scale-110 hover:shadow-md duration-200"
                      aria-label="Edit title"
                      title="Edit title"
                    >
                      <Edit2 className="w-3.5 h-3.5 text-gray-500 group-hover:text-blue-600" />
                      <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                        Edit title
                      </span>
                    </button>
                  </div>
                )
              )}

              {!isHeaderMinimized && currentTemplateName && (
                <div className="flex items-center mt-0 pl-1 max-w-full overflow-hidden flex-wrap">
                  <div className="flex items-center flex-wrap">
                    <FileText className="w-3 h-3 text-gray-400 mr-1.5 flex-shrink-0" />
                    <span className="text-xs font-medium text-gray-600 truncate max-w-[150px] sm:max-w-[250px] md:max-w-full" style={{ fontFamily: '-apple-system, "SF Pro Text", "San Francisco", BlinkMacSystemFont, sans-serif', letterSpacing: '-0.01em' }}>{currentTemplateName}</span>
                    {duration > 0 && (
                      <>
                        <span className="text-gray-400 font-bold mx-1.5 flex-shrink-0" style={{ fontFamily: '-apple-system, "SF Pro Display", "San Francisco", BlinkMacSystemFont, sans-serif' }}>·</span>
                        <span className="text-xs font-medium text-blue-600 flex items-center flex-shrink-0" style={{ fontFamily: '-apple-system, "SF Pro Display", "SF Pro Text", "San Francisco", BlinkMacSystemFont, sans-serif', letterSpacing: '-0.01em', fontWeight: 500 }}>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-2.5 w-2.5 mr-1 text-blue-500/90" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"></circle>
                            <polyline points="12 6 12 12 16 14"></polyline>
                          </svg>
                          {formatDuration(duration)}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Processing Options Display */}
              {!isHeaderMinimized && (notes?.processing_options?.visit_type || notes?.processing_options?.pronouns || notes?.processing_options?.note_length) && (
                <div className="flex items-center mt-1 pl-1 max-w-full overflow-hidden flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap text-xs">
                    {notes?.processing_options?.visit_type && (
                      <div className="flex items-center bg-blue-50 px-2 py-1 rounded-full">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-2.5 w-2.5 mr-1 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                          <circle cx="9" cy="7" r="4"></circle>
                          <path d="m22 21-3-3m1-3a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"></path>
                        </svg>
                        <span className="font-medium text-blue-700">{notes.processing_options.visit_type}</span>
                      </div>
                    )}

                    {notes?.processing_options?.pronouns && (
                      <div className="flex items-center bg-purple-50 px-2 py-1 rounded-full">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-2.5 w-2.5 mr-1 text-purple-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                          <circle cx="12" cy="7" r="4"></circle>
                        </svg>
                        <span className="font-medium text-purple-700">{notes.processing_options.pronouns}</span>
                      </div>
                    )}

                    {notes?.processing_options?.note_length && (
                      <div className="flex items-center bg-green-50 px-2 py-1 rounded-full">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-2.5 w-2.5 mr-1 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                          <polyline points="14 2 14 8 20 8"></polyline>
                          <line x1="16" y1="13" x2="8" y2="13"></line>
                          <line x1="16" y1="17" x2="8" y2="17"></line>
                          <polyline points="10 9 9 9 8 9"></polyline>
                        </svg>
                        <span className="font-medium text-green-700">{notes.processing_options.note_length}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              </div>
                    </div>

          {/* Actions - Recording Versions & Minimize/Maximize Toggle */}
          <div className="flex items-center gap-3">
            {/* Recording Versions */}
            {!isHeaderMinimized && selectedRecordingId && selectedRecordingId !== "current" && (
              <RecordingVersions
                recordingId={selectedRecordingId}
                selectedTransactionId={selectedTransactionId}
                onVersionSelect={(recordingId, versionId, isDefault) => {
                  console.log('NotesInterface - onVersionSelect called with:');
                  console.log('  - recordingId from version:', recordingId);
                  console.log('  - versionId:', versionId);
                  console.log('  - isDefault:', isDefault);
                  console.log('  - Current selectedRecordingId:', selectedRecordingId);

                  if (onVersionSelect) {
                    // Pass the recording_id from the selected version, not the original recording_id
                    onVersionSelect(recordingId, versionId, isDefault);
                  }
                }}
                onVersionUpdate={() => {
                  // When a version is set as default, refresh the current recording
                  if (onRefreshRecording) {
                    onRefreshRecording();
                  }
                }}
              />
            )}

            {/* Full Screen Toggle Button */}
            <button
              onClick={() => setIsFullScreen(!isFullScreen)}
              className="p-2 bg-gradient-to-r from-purple-50 to-purple-100 hover:from-purple-100 hover:to-purple-200 rounded-lg transition-all duration-200 flex items-center justify-center group shadow-sm hover:shadow-md"
              aria-label={isFullScreen ? "Exit full screen" : "Enter full screen"}
              title={isFullScreen ? "Exit full screen" : "Enter full screen"}
            >
              {isFullScreen ? (
                <Minimize className="w-5 h-5 text-purple-600 group-hover:text-purple-700" />
              ) : (
                <Maximize className="w-5 h-5 text-purple-600 group-hover:text-purple-700" />
              )}
            </button>

            {/* Minimize/Maximize Toggle Button */}
            <button
              onClick={() => setIsHeaderMinimized(!isHeaderMinimized)}
              className="p-2 bg-gradient-to-r from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 rounded-lg transition-all duration-200 flex items-center justify-center group shadow-sm hover:shadow-md"
              aria-label={isHeaderMinimized ? "Expand header" : "Collapse header"}
              title={isHeaderMinimized ? "Expand header" : "Collapse header"}
            >
              {isHeaderMinimized ? (
                <ChevronDown className="w-5 h-5 text-blue-600 group-hover:text-blue-700" />
              ) : (
                <ChevronUp className="w-5 h-5 text-blue-600 group-hover:text-blue-700" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      {!isHeaderMinimized && (
        <div className="bg-white/80 backdrop-blur-xl shadow-sm border-b border-[#E5E5EA]/50 overflow-x-auto z-10">
          <div className="max-w-[1200px] mx-auto">
            <div className="flex px-4 min-w-max">
              {tabs.map((tab) => (
                <TabButton
                  key={tab.id}
                  id={tab.id}
                  label={tab.label}
                  icon={tab.icon}
                  activeTab={activeTab}
                  onClick={setActiveTab}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto relative">
        <div className="max-w-[1200px] mx-auto px-4 py-8 pb-24">
          <AnimatePresence mode="wait">
            <motion.div
              key={`${activeTab}-${selectedRecordingId}`}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
            >
              {/* SOAP Notes Tab */}
              {activeTab === "soap" && (
                <div className="relative">
                  {recordingStatus === "completed" && processedContent && Object.keys(processedContent).length > 0 ? (
                    <>
                      <ProcessedContentView 
                        content={processedContent} 
                        readOnly={readOnly}
                        recordingId={selectedRecordingId}
                        activeTab={activeTab}
                        fullNotes={notes}

                        onContentChange={(updatedContent) => {
                          // Update local state first
                          let updatedProcessedContent;
                          
                          if (notes.processed_content?.scribed) {
                            updatedProcessedContent = {
                              ...notes.processed_content,
                              scribed: updatedContent
                            };
                          } else if (notes.processed_content?.content) {
                            updatedProcessedContent = {
                              ...notes.processed_content,
                              content: updatedContent
                            };
                          } else {
                            // Direct assignment if no nested structure
                            updatedProcessedContent = updatedContent;
                          }
                          
                          // Update the notes state locally
                          setNotes({
                            ...notes,
                            processed_content: updatedProcessedContent
                          });
                          
                          // Notify parent component of the change
                          if (onNotesChange) {
                            onNotesChange("processed_content", JSON.stringify(updatedProcessedContent));
                          }
                        }}
                      />
                    </>
                  ) : (
                    <>
                      <div className="text-center text-gray-500 py-16 bg-gray-50 rounded-lg">
                        <div className="bg-white p-8 max-w-md mx-auto rounded-xl shadow-sm border border-gray-100">
                          {recordingStatus === "analyzing" || recordingStatus === "processing" || recordingStatus === "transcript_ready" ? (
                            <>
                              <div className="w-16 h-16 mx-auto mb-4 rounded-full border-4 border-blue-500 border-t-transparent animate-spin" />
                              <p className="text-xl font-medium text-gray-700 mt-3">
                                {recordingStatus === "analyzing" ? "Analyzing recording..." : 
                                 recordingStatus === "transcript_ready" ? "Generating AI content..." :
                                 "Processing your recording..."}
                              </p>
                              <p className="text-sm text-gray-500 mt-2">
                                {recordingStatus === "analyzing" ? "Our AI is analyzing your recording to extract medical information" : 
                                 recordingStatus === "transcript_ready" ? "Converting transcript to structured SOAP notes" :
                                 "Please wait while we process your recording"}
                              </p>
                              <div className="mt-6 bg-blue-50 rounded-lg p-4 text-sm text-blue-700">
                                <p className="mb-1 font-medium">This may take a few moments</p>
                                <p>The processing time depends on the length of your recording.</p>
                              </div>
                            </>
                          ) : recordingStatus === "failed" ? (
                            <>
                              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                                <X className="w-8 h-8 text-red-500" />
                              </div>
                              <p className="text-xl font-medium text-gray-700 mt-3">Processing Failed</p>
                              <p className="text-sm text-gray-500 mt-2">There was an error processing your recording</p>
                              <button
                                onClick={handleRetry}
                                className="mt-6 text-sm text-blue-600 hover:text-blue-800 font-medium"
                              >
                                Try Again
                              </button>
                            </>
                          ) : (
                            <>
                              <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                              <p className="text-xl font-medium text-gray-700 mt-3">No notes available</p>
                              <p className="text-sm text-gray-400 mt-2">Start recording or upload an audio file to generate notes</p>
                            </>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Email Tab */}
              {activeTab === "email" && (
                <div className="relative p-6">
                  {recordingStatus === "completed" ? (
                  <div className="max-w-4xl mx-auto">
                    <div className="bg-white/70 backdrop-blur-xl rounded-2xl shadow-lg border border-[#E5E5EA] overflow-hidden">
                      {/* Email Header */}
                      <div className="px-6 py-4 bg-[#F2F2F7] border-b border-[#E5E5EA]">
                        <h3 className="text-lg font-medium text-[#1D1D1F]">
                          New Message
                        </h3>
                      </div>

                      {/* Email Form */}
                      <div className="divide-y divide-[#E5E5EA]">
                        {/* Recipients */}
                        <div className="px-6 py-3 flex items-center gap-3">
                          <span className="text-sm text-[#86868B] font-medium w-16">
                            To:
                          </span>
                          <input
                            type="text"
                            placeholder="Add recipients"
                            className="flex-1 text-sm text-[#1D1D1F] placeholder-[#86868B] focus:outline-none"
                            readOnly={readOnly}
                          />
                        </div>

                        {/* Subject */}
                        <div className="px-6 py-3 flex items-center gap-3">
                          <span className="text-sm text-[#86868B] font-medium w-16">
                            Subject:
                          </span>
                          <input
                            type="text"
                            value={emailData.subject}
                            placeholder="Add a subject"
                            className="flex-1 text-sm text-[#1D1D1F] placeholder-[#86868B] focus:outline-none"
                            readOnly={readOnly}
                            onChange={(e) => {
                              setEmailSubject(e.target.value);
                              // Update subject in notes
                              if (onNotesChange) {
                                onNotesChange("emailSubject", e.target.value);
                              }
                            }}
                          />
                        </div>

                        {/* Message Body */}
                        <div className="px-6 py-4">
                          <textarea
                            value={emailData.body.replace(/\\n/g, '\n')}
                            onChange={(e) => {
                              setEmailContent(e.target.value);
                              if (onNotesChange) {
                                onNotesChange("email", e.target.value);
                              }
                            }}
                            placeholder="Write your message here..."
                            readOnly={readOnly}
                            className="w-full h-[400px] text-sm text-[#1D1D1F] placeholder-[#86868B] focus:outline-none resize-none p-2 border border-transparent focus:border-blue-200 rounded whitespace-pre-wrap"
                          />
                        </div>
                      </div>

                    </div>
                  </div>
                  ) : (
                    <div className="text-center text-gray-500 py-16 bg-gray-50 rounded-lg">
                      <div className="bg-white p-8 max-w-md mx-auto rounded-xl shadow-sm border border-gray-100">
                        {recordingStatus === "analyzing" || recordingStatus === "processing" || recordingStatus === "transcript_ready" ? (
                          <>
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full border-4 border-blue-500 border-t-transparent animate-spin" />
                            <p className="text-xl font-medium text-gray-700 mt-3">
                              {recordingStatus === "analyzing" ? "Analyzing recording..." : 
                               recordingStatus === "transcript_ready" ? "Generating email content..." :
                               "Processing your recording..."}
                            </p>
                            <p className="text-sm text-gray-500 mt-2">
                              {recordingStatus === "analyzing" ? "Our AI is analyzing your recording to extract email information" : 
                               recordingStatus === "transcript_ready" ? "Converting transcript to patient email template" :
                               "Please wait while we process your recording"}
                            </p>
                          </>
                        ) : recordingStatus === "failed" ? (
                          <>
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                              <X className="w-8 h-8 text-red-500" />
                            </div>
                            <p className="text-xl font-medium text-gray-700 mt-3">Processing Failed</p>
                            <p className="text-sm text-gray-500 mt-2">There was an error processing your recording</p>
                            <button
                              onClick={handleRetry}
                              className="mt-6 text-sm text-blue-600 hover:text-blue-800 font-medium"
                            >
                              Try Again
                            </button>
                          </>
                        ) : (
                          <>
                            <Mail className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                            <p className="text-xl font-medium text-gray-700 mt-3">No email content available</p>
                            <p className="text-sm text-gray-400 mt-2">Email content will be generated after processing is complete</p>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Codes Tab */}
              {activeTab === "codes" && (
                <div className="relative">
                  {recordingStatus === "completed" ? (
                  <div className="max-w-6xl mx-auto p-6">
                    {/* Header with Summary Stats */}
                    <div className="mb-8">
                      <div className="text-center mb-6">
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">Medical Codes</h2>
                        <p className="text-gray-600">Automatically extracted from clinical documentation</p>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-sm font-semibold text-blue-700 uppercase tracking-wide">ICD-10 Codes</h3>
                              <p className="text-2xl font-bold text-blue-800">{icdCodes.length}</p>
                              <p className="text-sm text-blue-600">Diagnosis codes</p>
                            </div>
                            <div className="w-12 h-12 bg-blue-200 rounded-lg flex items-center justify-center">
                              <Code className="w-6 h-6 text-blue-700" />
                            </div>
                          </div>
                        </div>
                        
                        <div className="bg-gradient-to-r from-purple-50 to-purple-100 border border-purple-200 rounded-xl p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-sm font-semibold text-purple-700 uppercase tracking-wide">CPT Codes</h3>
                              <p className="text-2xl font-bold text-purple-800">{cptCodes.length}</p>
                              <p className="text-sm text-purple-600">Procedure codes</p>
                            </div>
                            <div className="w-12 h-12 bg-purple-200 rounded-lg flex items-center justify-center">
                              <Code className="w-6 h-6 text-purple-700" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-10">
                      {/* ICD Codes Section */}
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                        className="space-y-5"
                      >
                        <div className="bg-white rounded-xl border border-blue-200 overflow-hidden">
                          {/* Sticky Header with Gradient Background */}
                          <div className="sticky top-0 z-20 bg-gradient-to-r from-blue-50 to-blue-100 backdrop-blur-sm border-b border-blue-200 shadow-sm">
                            <div className="p-6">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center shadow-sm">
                                    <Code className="w-5 h-5 text-white" />
                                  </div>
                                  <div>
                                    <h3 className="text-xl font-bold text-gray-900">ICD-10 Diagnosis Codes</h3>
                                    <p className="text-sm text-gray-700">International Classification of Diseases codes for diagnoses</p>
                                  </div>
                                </div>
                                <button
                                  onClick={() => setIsICDSectionOpen(!isICDSectionOpen)}
                                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-800 bg-white/80 hover:bg-white border border-blue-200 hover:border-blue-300 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
                                >
                                  {isICDSectionOpen ? (
                                    <>
                                      <ChevronUp className="w-4 h-4" />
                                      Hide Codes
                                    </>
                                  ) : (
                                    <>
                                      <ChevronDown className="w-4 h-4" />
                                      Show Codes
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>

                          {/* Scrollable Content Area */}
                          <div className="relative">
                            <AnimatePresence>
                              {isICDSectionOpen && (
                                <motion.div
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: "auto" }}
                                  exit={{ opacity: 0, height: 0 }}
                                  transition={{ duration: 0.3 }}
                                  className="max-h-[60vh] overflow-y-auto bg-white"
                                >
                                  <div className="p-6">
                                    {/* Search and Add Controls */}
                                    <div className="mb-6 space-y-4">
                                      <div className="flex items-center gap-3">
                                        <div className="relative flex-1">
                                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                          <input
                                            type="text"
                                            value={icdSearchTerm}
                                            onChange={(e) => setIcdSearchTerm(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && searchMedicalCodes(icdSearchTerm, 'icd')}
                                            placeholder="Search ICD-10 codes... (e.g., 'back pain', 'M54')"
                                            className="w-full pl-10 pr-4 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                                          />
                                        </div>
                                        <button
                                          onClick={() => searchMedicalCodes(icdSearchTerm, 'icd')}
                                          disabled={!icdSearchTerm.trim() || isSearchingCodes.icd}
                                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                                        >
                                          {isSearchingCodes.icd ? "Searching..." : "Search"}
                                        </button>
                                        <button
                                          onClick={() => setShowAddIcdForm(!showAddIcdForm)}
                                          className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-lg transition-colors text-sm font-medium"
                                        >
                                          <Plus className="w-4 h-4" />
                                          Add Code
                                        </button>
                                      </div>
                                      
                                      {/* Search Results */}
                                      <SearchResults 
                                        results={searchResults.icd}
                                        onAddCode={addCodeToList}
                                        codeType="icd"
                                        isSearching={isSearchingCodes.icd}
                                        hasSearched={hasSearchedCodes.icd}
                                        colorTheme={{
                                          badge: "bg-blue-100 text-blue-800",
                                          button: "text-blue-700 bg-blue-100 hover:bg-blue-200"
                                        }}
                                      />
                                      
                                      {/* Add Code Form */}
                                      {showAddIcdForm && (
                                        <AddCodeForm
                                          codeType="icd"
                                          onAddCode={addCodeToList}
                                          onCancel={() => setShowAddIcdForm(false)}
                                          colorTheme={{
                                            headerBg: "bg-blue-50",
                                            button: "text-blue-700 bg-blue-100 hover:bg-blue-200"
                                          }}
                                        />
                                      )}
                                    </div>

                                    {/* Existing Codes */}
                                    {icdCodes.length > 0 ? (
                                      <div className="space-y-4">
                                        <div className="border-t border-gray-200 pt-4">
                                          <h4 className="text-sm font-semibold text-gray-700 mb-4">Current ICD-10 Codes</h4>
                                        </div>
                                        {icdCodes.map((code, index) => (
                                          <CodeItem
                                            key={index}
                                            code={code}
                                            color="[#0071E3]"
                                            onCopy={(content) => handleCopy(content, "icd")}
                                            onRemove={() => removeCodeFromList(index, "icd")}
                                          />
                                        ))}
                                      </div>
                                    ) : (
                                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-8 text-center">
                                        <Code className="w-12 h-12 mx-auto text-blue-400 mb-3" />
                                        <p className="text-blue-700 font-medium">No ICD codes identified</p>
                                        <p className="text-blue-600 text-sm mt-1">Search and add diagnosis codes using the tools above</p>
                                      </div>
                                    )}
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        </div>
                      </motion.div>

                      {/* CPT Codes Section */}
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.1 }}
                        className="space-y-5"
                      >
                        <div className="bg-white rounded-xl border border-purple-200 overflow-hidden">
                          {/* Sticky Header with Gradient Background */}
                          <div className="sticky top-0 z-20 bg-gradient-to-r from-purple-50 to-purple-100 backdrop-blur-sm border-b border-purple-200 shadow-sm">
                            <div className="p-6">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center shadow-sm">
                                    <Code className="w-5 h-5 text-white" />
                                  </div>
                                  <div>
                                    <h3 className="text-xl font-bold text-gray-900">CPT Procedure Codes</h3>
                                    <p className="text-sm text-gray-700">Current Procedural Terminology codes for procedures and services</p>
                                  </div>
                                </div>
                                <button
                                  onClick={() => setIsCPTSectionOpen(!isCPTSectionOpen)}
                                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-purple-800 bg-white/80 hover:bg-white border border-purple-200 hover:border-purple-300 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
                                >
                                  {isCPTSectionOpen ? (
                                    <>
                                      <ChevronUp className="w-4 h-4" />
                                      Hide Codes
                                    </>
                                  ) : (
                                    <>
                                      <ChevronDown className="w-4 h-4" />
                                      Show Codes
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>

                          {/* Scrollable Content Area */}
                          <div className="relative">
                            <AnimatePresence>
                              {isCPTSectionOpen && (
                                <motion.div
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: "auto" }}
                                  exit={{ opacity: 0, height: 0 }}
                                  transition={{ duration: 0.3 }}
                                  className="max-h-[60vh] overflow-y-auto bg-white"
                                >
                                  <div className="p-6">
                                    {/* Search and Add Controls */}
                                    <div className="mb-6 space-y-4">
                                      <div className="flex items-center gap-3">
                                        <div className="relative flex-1">
                                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                          <input
                                            type="text"
                                            value={cptSearchTerm}
                                            onChange={(e) => setCptSearchTerm(e.target.value)}
                                            onKeyPress={(e) => e.key === 'Enter' && searchMedicalCodes(cptSearchTerm, 'cpt')}
                                            placeholder="Search CPT codes... (e.g., 'office visit', '99214')"
                                            className="w-full pl-10 pr-4 py-2 border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                                          />
                                        </div>
                                        <button
                                          onClick={() => searchMedicalCodes(cptSearchTerm, 'cpt')}
                                          disabled={!cptSearchTerm.trim() || isSearchingCodes.cpt}
                                          className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                                        >
                                          {isSearchingCodes.cpt ? "Searching..." : "Search"}
                                        </button>
                                        <button
                                          onClick={() => setShowAddCptForm(!showAddCptForm)}
                                          className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 hover:bg-purple-200 rounded-lg transition-colors text-sm font-medium"
                                        >
                                          <Plus className="w-4 h-4" />
                                          Add Code
                                        </button>
                                      </div>
                                      
                                      {/* Search Results */}
                                      <SearchResults 
                                        results={searchResults.cpt}
                                        onAddCode={addCodeToList}
                                        codeType="cpt"
                                        isSearching={isSearchingCodes.cpt}
                                        hasSearched={hasSearchedCodes.cpt}
                                        colorTheme={{
                                          badge: "bg-purple-100 text-purple-800",
                                          button: "text-purple-700 bg-purple-100 hover:bg-purple-200"
                                        }}
                                      />
                                      
                                      {/* Add Code Form */}
                                      {showAddCptForm && (
                                        <AddCodeForm
                                          codeType="cpt"
                                          onAddCode={addCodeToList}
                                          onCancel={() => setShowAddCptForm(false)}
                                          colorTheme={{
                                            headerBg: "bg-purple-50",
                                            button: "text-purple-700 bg-purple-100 hover:bg-purple-200"
                                          }}
                                        />
                                      )}
                                    </div>

                                    {/* Existing Codes */}
                                    {cptCodes.length > 0 ? (
                                      <div className="space-y-4">
                                        <div className="border-t border-gray-200 pt-4">
                                          <h4 className="text-sm font-semibold text-gray-700 mb-4">Current CPT Codes</h4>
                                        </div>
                                        {cptCodes.map((code, index) => (
                                          <CodeItem
                                            key={index}
                                            code={code}
                                            color="[#5856D6]"
                                            onCopy={(content) => handleCopy(content, "cpt")}
                                            onRemove={() => removeCodeFromList(index, "cpt")}
                                          />
                                        ))}
                                      </div>
                                    ) : (
                                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-8 text-center">
                                        <Code className="w-12 h-12 mx-auto text-purple-400 mb-3" />
                                        <p className="text-purple-700 font-medium">No CPT codes identified</p>
                                        <p className="text-purple-600 text-sm mt-1">Search and add procedure codes using the tools above</p>
                                      </div>
                                    )}
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        </div>
                      </motion.div>

                      {/* Code Audit Analysis Section */}
                      {auditData && (
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.4, delay: 0.2 }}
                          className="space-y-5"
                        >
                          <div className="bg-white rounded-xl border border-orange-200 overflow-hidden">
                            {/* Sticky Header with Gradient Background */}
                            <div className="sticky top-0 z-20 bg-gradient-to-r from-orange-50 to-orange-100 backdrop-blur-sm border-b border-orange-200 shadow-sm">
                              <div className="p-6">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center shadow-sm">
                                      <Shield className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                      <h3 className="text-xl font-bold text-gray-900">Code Audit & Compliance</h3>
                                      <p className="text-sm text-gray-700">Billing audit analysis and risk assessment</p>
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => setIsAuditSectionOpen(!isAuditSectionOpen)}
                                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-orange-800 bg-white/80 hover:bg-white border border-orange-200 hover:border-orange-300 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
                                  >
                                    {isAuditSectionOpen ? (
                                      <>
                                        <ChevronUp className="w-4 h-4" />
                                        Hide Audit
                                      </>
                                    ) : (
                                      <>
                                        <ChevronDown className="w-4 h-4" />
                                        Show Audit
                                      </>
                                    )}
                                  </button>
                                </div>
                              </div>
                            </div>

                            {/* Scrollable Content Area */}
                            <div className="relative">

                            <AnimatePresence>
                              {isAuditSectionOpen && (
                                <motion.div
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: "auto" }}
                                  exit={{ opacity: 0, height: 0 }}
                                  transition={{ duration: 0.3 }}
                                  className="max-h-[60vh] overflow-y-auto bg-white"
                                >
                                  <div className="p-6">
                                  {/* Risk Summary Cards */}
                                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                      <div className="flex items-center gap-2 mb-2">
                                        <TrendingDown className="w-4 h-4 text-green-600" />
                                        <span className="text-sm font-semibold text-green-700">Overcoding Risk</span>
                                      </div>
                                      <p className="text-lg font-bold text-green-800">{auditData.risk_summary?.overcoding_risk || 'N/A'}</p>
                                    </div>
                                    
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                      <div className="flex items-center gap-2 mb-2">
                                        <TrendingUp className="w-4 h-4 text-blue-600" />
                                        <span className="text-sm font-semibold text-blue-700">Undercoding Risk</span>
                                      </div>
                                      <p className="text-lg font-bold text-blue-800">{auditData.risk_summary?.undercoding_risk || 'N/A'}</p>
                                    </div>
                                    
                                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                                      <div className="flex items-center gap-2 mb-2">
                                        <FileText className="w-4 h-4 text-purple-600" />
                                        <span className="text-sm font-semibold text-purple-700">Documentation</span>
                                      </div>
                                      <p className="text-lg font-bold text-purple-800">{auditData.risk_summary?.documentation_adequacy || 'N/A'}</p>
                                    </div>
                                    
                                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                                      <div className="flex items-center gap-2 mb-2">
                                        <Shield className="w-4 h-4 text-orange-600" />
                                        <span className="text-sm font-semibold text-orange-700">Compliance</span>
                                      </div>
                                      <p className="text-lg font-bold text-orange-800">{auditData.risk_summary?.overall_compliance || 'N/A'}</p>
                                    </div>
                                  </div>

                                  {/* Audit Table */}
                                  <div className="bg-gray-50 rounded-lg p-4 mb-6">
                                    <h4 className="text-lg font-semibold text-gray-900 mb-4">Code-by-Code Audit Results</h4>
                                    <div className="overflow-x-auto">
                                      <table className="w-full text-sm">
                                        <thead>
                                          <tr className="border-b border-gray-200">
                                            <th className="text-left py-3 px-4 font-semibold text-gray-700">Code</th>
                                            <th className="text-left py-3 px-4 font-semibold text-gray-700">Audit Status</th>
                                            <th className="text-left py-3 px-4 font-semibold text-gray-700">Issue</th>
                                            <th className="text-left py-3 px-4 font-semibold text-gray-700">Evidence</th>
                                            <th className="text-left py-3 px-4 font-semibold text-gray-700">Comment</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {(auditData.code_audit || []).map((item, index) => (
                                            <tr key={index} className="border-b border-gray-100 hover:bg-white transition-colors">
                                              <td className="py-3 px-4">
                                                <span className="font-mono font-semibold text-blue-700">{item.code}</span>
                                              </td>
                                              <td className="py-3 px-4">
                                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                                                  item.audit_status?.includes('✅')
                                                    ? 'bg-green-100 text-green-800'
                                                    : item.audit_status?.includes('🔶')
                                                    ? 'bg-yellow-100 text-yellow-800'
                                                    : 'bg-red-100 text-red-800'
                                                }`}>
                                                  {item.audit_status}
                                                </span>
                                              </td>
                                              <td className="py-3 px-4">
                                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                                  item.audit_issue === 'Correct' 
                                                    ? 'bg-green-50 text-green-700' 
                                                    : 'bg-yellow-50 text-yellow-700'
                                                }`}>
                                                  {item.audit_issue}
                                                </span>
                                              </td>
                                              <td className="py-3 px-4">
                                                <span className={`px-2 py-1 rounded text-xs ${
                                                  item.evidence_strength === 'Strong' 
                                                    ? 'bg-blue-50 text-blue-700' 
                                                    : 'bg-gray-50 text-gray-700'
                                                }`}>
                                                  {item.evidence_strength}
                                                </span>
                                              </td>
                                              <td className="py-3 px-4 max-w-md">
                                                <p className="text-gray-700 text-xs leading-relaxed">{item.comment}</p>
                                                {item.recommendation && item.recommendation !== 'None' && (
                                                  <p className="text-orange-600 text-xs mt-1 font-medium">
                                                    💡 {item.recommendation}
                                                  </p>
                                                )}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>

                                                                    {/* Recommendations */}
                                  {auditData.risk_summary?.recommendations && auditData.risk_summary.recommendations.length > 0 && (
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                                      <h4 className="text-sm font-semibold text-blue-900 mb-3 flex items-center gap-2">
                                        <AlertCircle className="w-4 h-4" />
                                        Recommendations
                                      </h4>
                                      <ul className="space-y-2">
                                        {auditData.risk_summary.recommendations.map((rec, index) => (
                                          <li key={index} className="text-sm text-blue-800 flex items-start gap-2">
                                            <span className="text-blue-500 mt-0.5">•</span>
                                            <span>{typeof rec === 'object' ? (rec.description || rec.cpt_code || JSON.stringify(rec)) : String(rec)}</span>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}


                                   </div>
                                 </motion.div>
                               )}
                             </AnimatePresence>
                             </div>
                           </div>
                        </motion.div>
                      )}

                      {/* Fee Optimization Section */}
                      {auditData && auditData.fee_optimization && (
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.4, delay: 0.3 }}
                          className="space-y-5"
                        >
                          <div className="bg-white rounded-xl border border-green-200 overflow-hidden">
                            {/* Sticky Header with Gradient Background */}
                            <div className="sticky top-0 z-20 bg-gradient-to-r from-green-50 to-emerald-100 backdrop-blur-sm border-b border-green-200 shadow-sm">
                              <div className="p-6">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center shadow-sm">
                                      <DollarSign className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                      <h3 className="text-xl font-bold text-gray-900">Fee Optimization & Revenue Opportunities</h3>
                                      <p className="text-sm text-gray-700">Compliant opportunities to maximize revenue while maintaining billing integrity</p>
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => setIsFeeOptimizationOpen(!isFeeOptimizationOpen)}
                                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-green-800 bg-white/80 hover:bg-white border border-green-200 hover:border-green-300 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
                                  >
                                    {isFeeOptimizationOpen ? (
                                      <>
                                        <ChevronUp className="w-4 h-4" />
                                        Hide Optimization
                                      </>
                                    ) : (
                                      <>
                                        <ChevronDown className="w-4 h-4" />
                                        Show Optimization
                                      </>
                                    )}
                                  </button>
                                </div>
                              </div>
                            </div>

                            {/* Scrollable Content Area */}
                            <div className="relative">
                              <AnimatePresence>
                                {isFeeOptimizationOpen && (
                                  <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: "auto" }}
                                    exit={{ opacity: 0, height: 0 }}
                                    transition={{ duration: 0.3 }}
                                    className="max-h-[60vh] overflow-y-auto bg-white"
                                  >
                                    <div className="p-6">
                                      {/* Revenue Summary Cards */}
                                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                          <div className="flex items-center gap-2 mb-2">
                                            <Target className="w-4 h-4 text-gray-600" />
                                            <span className="text-sm font-semibold text-gray-700">Current Total</span>
                                          </div>
                                          <p className="text-xl font-bold text-gray-800">{auditData.fee_optimization.revenue_summary?.current_total || 'N/A'}</p>
                                        </div>
                                        
                                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                          <div className="flex items-center gap-2 mb-2">
                                            <ArrowUpRight className="w-4 h-4 text-green-600" />
                                            <span className="text-sm font-semibold text-green-700">Optimized Total</span>
                                          </div>
                                          <p className="text-xl font-bold text-green-800">{auditData.fee_optimization.revenue_summary?.optimized_total || 'N/A'}</p>
                                        </div>
                                        
                                        <div className="bg-gradient-to-r from-green-100 to-emerald-100 border border-green-300 rounded-lg p-4">
                                          <div className="flex items-center gap-2 mb-2">
                                            <TrendingUp className="w-4 h-4 text-emerald-600" />
                                            <span className="text-sm font-semibold text-emerald-700">Revenue Boost</span>
                                          </div>
                                          <p className="text-xl font-bold text-emerald-800">
                                            {auditData.fee_optimization.revenue_summary?.revenue_boost || 'N/A'}
                                            <span className="text-sm ml-2 text-emerald-600">
                                              ({auditData.fee_optimization.revenue_summary?.boost_percentage || 'N/A'})
                                            </span>
                                          </p>
                                        </div>
                                      </div>

                                      {/* Fee Optimization Table */}
                                      <div className="bg-gray-50 rounded-lg p-4 mb-6">
                                        <h4 className="text-lg font-semibold text-gray-900 mb-4">Optimization Analysis</h4>
                                        <div className="overflow-x-auto">
                                          <table className="w-full text-sm">
                                            <thead>
                                              <tr className="border-b border-gray-200">
                                                <th className="text-left py-3 px-4 font-semibold text-gray-700">Current CPT</th>
                                                <th className="text-left py-3 px-4 font-semibold text-gray-700">Current Fee</th>
                                                <th className="text-left py-3 px-4 font-semibold text-gray-700">Suggestion</th>
                                                <th className="text-left py-3 px-4 font-semibold text-gray-700">Potential CPT</th>
                                                <th className="text-left py-3 px-4 font-semibold text-gray-700">Potential Fee</th>
                                                <th className="text-left py-3 px-4 font-semibold text-gray-700">Justification</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {(auditData.fee_optimization.optimization_table || []).map((item, index) => (
                                                <tr key={index} className="border-b border-gray-100 hover:bg-white transition-colors">
                                                  <td className="py-3 px-4">
                                                    <span className="font-mono font-semibold text-blue-700">{item.current_cpt}</span>
                                                  </td>
                                                  <td className="py-3 px-4">
                                                    <span className="text-gray-800 font-medium">{item.current_fee}</span>
                                                  </td>
                                                  <td className="py-3 px-4 max-w-sm">
                                                    <p className="text-gray-700 text-xs leading-relaxed">{item.suggestion}</p>
                                                  </td>
                                                  <td className="py-3 px-4">
                                                    {item.suggested_cpt !== 'N/A' ? (
                                                      <span className="font-mono font-semibold text-green-700 bg-green-100 px-2 py-1 rounded">
                                                        {item.suggested_cpt}
                                                      </span>
                                                    ) : (
                                                      <span className="text-gray-400 text-sm">N/A</span>
                                                    )}
                                                  </td>
                                                  <td className="py-3 px-4">
                                                    {item.suggested_fee !== 'N/A' ? (
                                                      <span className="text-green-800 font-bold">{item.suggested_fee}</span>
                                                    ) : (
                                                      <span className="text-gray-400 text-sm">N/A</span>
                                                    )}
                                                  </td>
                                                  <td className="py-3 px-4 max-w-md">
                                                    <p className="text-gray-700 text-xs leading-relaxed">{item.justification}</p>
                                                    {item.documentation_needed && item.documentation_needed !== 'N/A' && (
                                                      <p className="text-amber-600 text-xs mt-1 font-medium">
                                                        📋 {item.documentation_needed}
                                                      </p>
                                                    )}
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>

                                      {/* Additional Opportunities */}
                                      {auditData.fee_optimization.additional_opportunities && auditData.fee_optimization.additional_opportunities.length > 0 && (
                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                          <h5 className="text-sm font-semibold text-yellow-900 mb-3 flex items-center gap-2">
                                            <Target className="w-4 h-4" />
                                            Additional Revenue Opportunities
                                          </h5>
                                          <ul className="space-y-2">
                                            {auditData.fee_optimization.additional_opportunities.map((opp, index) => (
                                              <li key={index} className="text-sm text-yellow-800 flex items-start gap-2">
                                                <span className="text-yellow-600 mt-0.5">💰</span>
                                                <span>{typeof opp === 'object' ? (opp.description || opp.cpt_code || JSON.stringify(opp)) : String(opp)}</span>
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </div>
                  </div>
                  ) : (
                    <div className="text-center text-gray-500 py-16 bg-gray-50 rounded-lg">
                      <div className="bg-white p-8 max-w-md mx-auto rounded-xl shadow-sm border border-gray-100">
                        {recordingStatus === "analyzing" || recordingStatus === "processing" || recordingStatus === "transcript_ready" ? (
                          <>
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full border-4 border-blue-500 border-t-transparent animate-spin" />
                            <p className="text-xl font-medium text-gray-700 mt-3">
                              {recordingStatus === "analyzing" ? "Analyzing recording..." : 
                               recordingStatus === "transcript_ready" ? "Generating medical codes..." :
                               "Processing your recording..."}
                            </p>
                            <p className="text-sm text-gray-500 mt-2">
                              {recordingStatus === "analyzing" ? "Our AI is analyzing your recording to extract medical codes" : 
                               recordingStatus === "transcript_ready" ? "Extracting ICD and CPT codes from transcript" :
                               "Please wait while we process your recording"}
                            </p>
                          </>
                        ) : recordingStatus === "failed" ? (
                          <>
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                              <X className="w-8 h-8 text-red-500" />
                            </div>
                            <p className="text-xl font-medium text-gray-700 mt-3">Processing Failed</p>
                            <p className="text-sm text-gray-500 mt-2">There was an error processing your recording</p>
                            <button
                              onClick={() => window.location.reload()}
                              className="mt-6 text-sm text-blue-600 hover:text-blue-800 font-medium"
                            >
                              Try Again
                            </button>
                          </>
                        ) : (
                          <>
                            <Code className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                            <p className="text-xl font-medium text-gray-700 mt-3">No codes available</p>
                            <p className="text-sm text-gray-400 mt-2">Medical codes will be generated after processing is complete</p>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Conversation Tab */}
              {activeTab === "conversation" && (
                <div className="relative">
                  {recordingStatus === "completed" ? (
                  <div className="max-w-4xl mx-auto p-6">
                    {notes.processed_content?.conversation ? (
                      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
                        <div className="p-6">
                          <h3 className="text-lg font-semibold text-gray-900 mb-4">
                            Conversation Transcript
                          </h3>
                          <ConversationView
                            conversation={notes.processed_content.conversation}
                          />
                        </div>
                      </div>
                    ) : (
                      <NoContentPlaceholder
                        icon={<MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-400" />}
                        title="No conversation available"
                        subtitle="Conversations will appear here once processed"
                      />
                    )}
                  </div>
                  ) : (
                    <div className="text-center text-gray-500 py-16 bg-gray-50 rounded-lg">
                      <div className="bg-white p-8 max-w-md mx-auto rounded-xl shadow-sm border border-gray-100">
                        {recordingStatus === "analyzing" || recordingStatus === "processing" || recordingStatus === "transcript_ready" ? (
                          <>
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full border-4 border-blue-500 border-t-transparent animate-spin" />
                            <p className="text-xl font-medium text-gray-700 mt-3">
                              {recordingStatus === "analyzing" ? "Analyzing recording..." : 
                               recordingStatus === "transcript_ready" ? "Generating conversation structure..." :
                               "Processing your recording..."}
                            </p>
                            <p className="text-sm text-gray-500 mt-2">
                              {recordingStatus === "analyzing" ? "Our AI is analyzing your recording to extract conversation" : 
                               recordingStatus === "transcript_ready" ? "Converting transcript to structured conversation" :
                               "Please wait while we process your recording"}
                            </p>
                          </>
                        ) : recordingStatus === "failed" ? (
                          <>
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                              <X className="w-8 h-8 text-red-500" />
                            </div>
                            <p className="text-xl font-medium text-gray-700 mt-3">Processing Failed</p>
                            <p className="text-sm text-gray-500 mt-2">There was an error processing your recording</p>
                            <button
                              onClick={() => window.location.reload()}
                              className="mt-6 text-sm text-blue-600 hover:text-blue-800 font-medium"
                            >
                              Try Again
                            </button>
                          </>
                        ) : (
                          <>
                            <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                            <p className="text-xl font-medium text-gray-700 mt-3">No conversation available</p>
                            <p className="text-sm text-gray-400 mt-2">Conversation will be generated after processing is complete</p>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Transcript Tab */}
              {activeTab === "transcript" && (
                <div className="relative">
                  {recordingStatus === "completed" || recordingStatus === "transcript_ready" ? (
                  <div className="max-w-4xl mx-auto p-6">
                    {notes?.transcript ? (
                      <TranscriptView
                        value={notes.transcript}
                        onChange={(value) => onNotesChange && onNotesChange("transcript", value)}
                        readOnly={readOnly}
                      />
                    ) : (
                      <NoContentPlaceholder
                        icon={<MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-400" />}
                        title="No transcript available"
                        subtitle="Transcripts will appear here once recorded"
                      />
                    )}
                  </div>
                  ) : (
                    <div className="text-center text-gray-500 py-16 bg-gray-50 rounded-lg">
                      <div className="bg-white p-8 max-w-md mx-auto rounded-xl shadow-sm border border-gray-100">
                        {recordingStatus === "analyzing" || recordingStatus === "processing" ? (
                          <>
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full border-4 border-blue-500 border-t-transparent animate-spin" />
                            <p className="text-xl font-medium text-gray-700 mt-3">
                              {recordingStatus === "analyzing" ? "Transcribing audio..." : 
                               "Processing your recording..."}
                            </p>
                            <p className="text-sm text-gray-500 mt-2">
                              {recordingStatus === "analyzing" ? "Our AI is converting your audio to text" : 
                               "Please wait while we process your recording"}
                            </p>
                          </>
                        ) : recordingStatus === "failed" ? (
                          <>
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                              <X className="w-8 h-8 text-red-500" />
                            </div>
                            <p className="text-xl font-medium text-gray-700 mt-3">Processing Failed</p>
                            <p className="text-sm text-gray-500 mt-2">There was an error processing your recording</p>
                            <button
                              onClick={() => window.location.reload()}
                              className="mt-6 text-sm text-blue-600 hover:text-blue-800 font-medium"
                            >
                              Try Again
                            </button>
                          </>
                        ) : (
                          <>
                            <MessageSquare className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                            <p className="text-xl font-medium text-gray-700 mt-3">No transcript available</p>
                            <p className="text-sm text-gray-400 mt-2">Transcript will be generated after processing is complete</p>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* AI Doctor Tab */}
              {activeTab === "aidoctor" && (
                <div className="relative">
                  {recordingStatus === "completed" ? (
                  <AIDoctorView 
                    key={`aidoctor-${selectedRecordingId}`}
                    aiDoctor={aiDoctorData} 
                    readOnly={Boolean(readOnly)}
                    recordingId={selectedRecordingId}
                    patientId={notes?.processing_options?.patient_id}
                    consultationDate={notes?.processing_options?.date || new Date().toISOString().split('T')[0]}
                    metadata={{
                      patient_name: notes?.processing_options?.patient_name,
                      visit_type: notes?.processing_options?.visit_type,
                      note_length: notes?.processing_options?.note_length,
                      provider: notes?.processing_options?.provider,
                      template_id: selectedTemplateId
                    }}
                    onSave={(updatedData) => {
                      if (onNotesChange) {
                        const updatedContent = {
                          ...notes.processed_content,
                          aiDoctor: updatedData.aiDoctor
                        };
                        setNotes({
                          ...notes,
                          processed_content: updatedContent
                        });
                        onNotesChange("processed_content", JSON.stringify(updatedContent));
                        
                        // Also save the complete data with metadata if needed
                        onNotesChange("aiDoctorComplete", JSON.stringify(updatedData));
                      }
                    }}
                  />
                  ) : (
                    <div className="text-center text-gray-500 py-16 bg-gray-50 rounded-lg">
                      <div className="bg-white p-8 max-w-md mx-auto rounded-xl shadow-sm border border-gray-100">
                        {recordingStatus === "analyzing" || recordingStatus === "processing" || recordingStatus === "transcript_ready" ? (
                          <>
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full border-4 border-blue-500 border-t-transparent animate-spin" />
                            <p className="text-xl font-medium text-gray-700 mt-3">
                              {recordingStatus === "analyzing" ? "Analyzing recording..." : 
                               recordingStatus === "transcript_ready" ? "Generating AI Doctor report..." :
                               "Processing your recording..."}
                            </p>
                            <p className="text-sm text-gray-500 mt-2">
                              {recordingStatus === "analyzing" ? "Our AI is analyzing your recording for medical insights" : 
                               recordingStatus === "transcript_ready" ? "Creating structured clinical assessment" :
                               "Please wait while we process your recording"}
                            </p>
                            <div className="mt-6 bg-blue-50 rounded-lg p-4 text-sm text-blue-700">
                              <p className="mb-1 font-medium">AI processing in progress</p>
                              <p>The AI Doctor report is being generated from your medical conversation</p>
                            </div>
                          </>
                        ) : recordingStatus === "failed" ? (
                          <>
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                              <X className="w-8 h-8 text-red-500" />
                            </div>
                            <p className="text-xl font-medium text-gray-700 mt-3">Processing Failed</p>
                            <p className="text-sm text-gray-500 mt-2">There was an error processing your recording</p>
                            <button
                              onClick={() => window.location.reload()}
                              className="mt-6 text-sm text-blue-600 hover:text-blue-800 font-medium"
                            >
                              Try Again
                            </button>
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                            <p className="text-xl font-medium text-gray-700 mt-3">No AI Doctor data available</p>
                            <p className="text-sm text-gray-400 mt-2">AI Doctor report will be generated after processing is complete</p>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Unified Bottom Bar for all tabs */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/80 backdrop-blur-xl border-t border-[#E5E5EA]/50 shadow-lg z-20">
        <div className="max-w-[1200px] mx-auto px-4 py-3 sm:py-4 flex flex-wrap justify-between items-center gap-2">
          <div className="flex items-center gap-3">
            <button
              onClick={handleCopyActiveTab}
              className="inline-flex items-center gap-1 sm:gap-2 px-3 sm:px-5 py-2 sm:py-2.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-xl transition-all duration-200 hover:shadow-sm"
            >
              {copyStatus.isCopied && copyStatus.type === activeTab ? (
                <>
                  <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="font-medium">Copied!</span>
                </>
              ) : (
                <>
                  <Copy className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="font-medium">
                    Copy {activeTab === "soap" ? "Notes" : 
                           activeTab === "email" ? "Email" : 
                           activeTab === "codes" ? "Codes" : 
                           activeTab === "transcript" ? "Transcript" : 
                           activeTab === "conversation" ? "Conversation" : 
                           activeTab === "aidoctor" ? "AI Doctor" : "All"}
                  </span>
                </>
              )}
            </button>


            
            {!readOnly && activeTab === "soap" && (
              <button
                onClick={() => setIsAIOptionsOpen(true)}
                className="inline-flex items-center gap-1.5 sm:gap-2 px-4 sm:px-6 py-2.5 sm:py-3 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 active:from-blue-800 active:to-blue-900 rounded-xl transition-all duration-200 shadow-lg shadow-blue-600/20 hover:shadow-xl hover:shadow-blue-600/30 transform hover:-translate-y-0.5"
              >
                <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="font-medium">AI Suggestions</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* AI Options Modal */}
      {isAIOptionsOpen && (
        <AIOptionsModal
          isOpen={isAIOptionsOpen}
          onClose={() => setIsAIOptionsOpen(false)}
          selectedRecordingId={selectedRecordingId}
          patientName={notes?.processing_options?.patient_name || ""}
          selectedVisitType={selectedVisitType}
          selectedPronouns={selectedPronouns}
          selectedNoteLength={selectedNoteLength}
          setNotes={(updatedNotes) => {
            setNotes((prev) => ({ ...prev, ...updatedNotes }));
            if (onNotesChange) {
              // Update the parent component's state as needed
              onNotesChange("notes", JSON.stringify(updatedNotes));
            }
          }}
          onError={onError}
        />
      )}


    </div>
  );
};